import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu, LayoutDashboard } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import Logo from "./Logo";
import { SERVICE_ROUTES } from "../../data/content";
import { useAuth } from "../../context/AuthContext";
import { useSettings } from "../../context/SettingsContext";

const LINKS = [
  { to: "/calculators", label: "Calculators" },
  { to: "/learn", label: "Learn" },
  { to: "/blog", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const linkCls = ({ isActive }) =>
  `text-sm font-medium transition-colors duration-200 ${isActive ? "text-gold" : "text-slate-300 hover:text-white"}`;

function PortalLink({ mobile }) {
  const { user } = useAuth();
  const to = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/login";
  return (
    <Link to={to} data-testid={mobile ? "mobile-portal-link" : "nav-portal-link"}
      className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-white">
      <LayoutDashboard size={16} /> {user ? "My Portal" : "Client Login"}
    </Link>
  );
}

function ServicesMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-services-trigger" className="inline-flex items-center gap-1 text-sm font-medium text-slate-300 outline-none hover:text-white">
        Services <ChevronDown size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60 border-white/10 bg-navy-800 text-slate-200">
        {SERVICE_ROUTES.map((s) => (
          <DropdownMenuItem key={s.slug} asChild className="cursor-pointer focus:bg-white/10 focus:text-white">
            <Link to={`/${s.slug}`} data-testid={`nav-service-${s.slug}`}>{s.label}</Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem asChild className="cursor-pointer text-gold focus:bg-white/10 focus:text-gold">
          <Link to="/services" data-testid="nav-all-services">All services</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu() {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger data-testid="mobile-menu-btn" className="text-white lg:hidden" aria-label="Open menu"><Menu /></SheetTrigger>
      <SheetContent side="right" className="border-white/10 bg-navy text-white">
        <SheetTitle className="text-white"><Logo /></SheetTitle>
        <nav className="mt-8 flex flex-col gap-4" onClick={() => setOpen(false)}>
          {[{ to: "/services", label: "All Services" }, ...SERVICE_ROUTES.map((s) => ({ to: `/${s.slug}`, label: s.label })), ...LINKS].map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`mobile-nav-${l.to.slice(1)}`}>{l.label}</NavLink>
          ))}
          <PortalLink mobile />
          <Link to="/book-consultation" data-testid="mobile-book-btn" className="mt-4 rounded-full bg-mint px-5 py-3 text-center text-sm font-semibold text-navy">Book Free Consultation</Link>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { settings } = useSettings();
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header data-testid="site-navbar" className={`sticky top-0 z-50 transition-[background-color,box-shadow] duration-300 ${scrolled ? "bg-navy/85 shadow-[0_8px_30px_rgba(0,0,0,0.25)] backdrop-blur-xl" : "bg-navy"}`}>
      {settings?.announcement && (
        <div data-testid="site-announcement" className="bg-gold px-4 py-1.5 text-center text-xs font-semibold text-navy">{settings.announcement}</div>
      )}
      <div className="container-q flex h-[72px] items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="Quantora home"><Logo /></Link>
        <nav className="hidden items-center gap-8 lg:flex">
          <ServicesMenu />
          {LINKS.map((l) => <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.to.slice(1)}`}>{l.label}</NavLink>)}
        </nav>
        <div className="hidden items-center gap-6 lg:flex">
          <PortalLink />
          <Link to="/book-consultation" data-testid="nav-book-btn"
            className="rounded-full bg-mint px-5 py-2.5 text-sm font-semibold text-navy transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#19dc9b]">
            Book Free Consultation
          </Link>
        </div>
        <MobileMenu />
      </div>
    </header>
  );
}
